/**
 * Utility functions for working with cron expressions
 */

export interface CronValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates a cron expression
 * @param expression The cron expression to validate
 * @returns Validation result with error message if invalid
 */
export function validateCronExpression(expression: string): CronValidationResult {
  const trimmed = expression.trim();
  
  if (!trimmed) {
    return { isValid: false, error: "Cron expression cannot be empty" };
  }

  // Split by whitespace and filter out empty parts
  const parts = trimmed.split(/\s+/).filter(part => part.length > 0);
  
  if (parts.length !== 5) {
    return { 
      isValid: false, 
      error: `Cron expression must have exactly 5 parts (minute hour day month weekday), found ${parts.length}` 
    };
  }

  const [minute, hour, day, month, weekday] = parts;

  // Validate each field
  const minuteValidation = validateCronField(minute, 0, 59, "minute");
  if (!minuteValidation.isValid) return minuteValidation;

  const hourValidation = validateCronField(hour, 0, 23, "hour");
  if (!hourValidation.isValid) return hourValidation;

  const dayValidation = validateCronField(day, 1, 31, "day");
  if (!dayValidation.isValid) return dayValidation;

  const monthValidation = validateCronField(month, 1, 12, "month");
  if (!monthValidation.isValid) return monthValidation;

  const weekdayValidation = validateCronField(weekday, 0, 7, "weekday");
  if (!weekdayValidation.isValid) return weekdayValidation;

  return { isValid: true };
}

/**
 * Validates a single cron field
 */
function validateCronField(field: string, min: number, max: number, fieldName: string): CronValidationResult {
  // Allow wildcard
  if (field === '*') {
    return { isValid: true };
  }

  // Check for valid characters
  if (!/^[0-9*,\-/]+$/.test(field)) {
    return { 
      isValid: false, 
      error: `Invalid characters in ${fieldName} field: ${field}` 
    };
  }

  // Handle ranges (e.g., 1-5)
  if (field.includes('-')) {
    const ranges = field.split(',');
    for (const range of ranges) {
      if (range.includes('-')) {
        const [start, end] = range.split('-');
        const startNum = parseInt(start);
        const endNum = parseInt(end);
        
        if (isNaN(startNum) || isNaN(endNum)) {
          return { isValid: false, error: `Invalid range in ${fieldName}: ${range}` };
        }
        
        if (startNum < min || startNum > max || endNum < min || endNum > max) {
          return { 
            isValid: false, 
            error: `${fieldName} range ${range} is outside valid range ${min}-${max}` 
          };
        }
        
        if (startNum > endNum) {
          return { 
            isValid: false, 
            error: `Invalid range in ${fieldName}: start (${startNum}) is greater than end (${endNum})` 
          };
        }
      } else {
        // Single number in comma-separated list
        const num = parseInt(range);
        if (isNaN(num) || num < min || num > max) {
          return { 
            isValid: false, 
            error: `${fieldName} value ${range} is outside valid range ${min}-${max}` 
          };
        }
      }
    }
    return { isValid: true };
  }

  // Handle step values (e.g., */5)
  if (field.includes('/')) {
    const [base, step] = field.split('/');
    const stepNum = parseInt(step);
    
    if (isNaN(stepNum) || stepNum <= 0) {
      return { isValid: false, error: `Invalid step value in ${fieldName}: ${step}` };
    }
    
    if (base !== '*') {
      const baseNum = parseInt(base);
      if (isNaN(baseNum) || baseNum < min || baseNum > max) {
        return { 
          isValid: false, 
          error: `${fieldName} base value ${base} is outside valid range ${min}-${max}` 
        };
      }
    }
    
    return { isValid: true };
  }

  // Handle comma-separated values
  if (field.includes(',')) {
    const values = field.split(',');
    for (const value of values) {
      const num = parseInt(value);
      if (isNaN(num) || num < min || num > max) {
        return { 
          isValid: false, 
          error: `${fieldName} value ${value} is outside valid range ${min}-${max}` 
        };
      }
    }
    return { isValid: true };
  }

  // Single number
  const num = parseInt(field);
  if (isNaN(num) || num < min || num > max) {
    return { 
      isValid: false, 
      error: `${fieldName} value ${field} is outside valid range ${min}-${max}` 
    };
  }

  return { isValid: true };
}

/**
 * Converts a cron expression to a human-readable description
 */
export function describeCronExpression(expression: string): string {
  const validation = validateCronExpression(expression);
  if (!validation.isValid) {
    return "Invalid cron expression";
  }

  const parts = expression.trim().split(/\s+/);
  const [minute, hour, day, month, weekday] = parts;

  // Handle some common patterns
  if (expression === "0 0 * * *") return "Every day at midnight";
  if (expression === "0 12 * * *") return "Every day at noon";
  if (expression === "0 9 * * 1-5") return "Weekdays at 9:00 AM";
  if (expression === "0 0 1 * *") return "First day of every month at midnight";
  if (expression === "0 */6 * * *") return "Every 6 hours";
  if (expression === "*/15 * * * *") return "Every 15 minutes";

  // Build description parts
  const parts_desc: string[] = [];

  // Minute
  if (minute === "*") {
    parts_desc.push("every minute");
  } else if (minute.includes("/")) {
    const [, step] = minute.split("/");
    parts_desc.push(`every ${step} minutes`);
  } else {
    parts_desc.push(`at minute ${minute}`);
  }

  // Hour
  if (hour !== "*") {
    if (hour.includes("/")) {
      const [, step] = hour.split("/");
      parts_desc.push(`every ${step} hours`);
    } else {
      parts_desc.push(`at hour ${hour}`);
    }
  }

  // Day
  if (day !== "*") {
    parts_desc.push(`on day ${day}`);
  }

  // Month
  if (month !== "*") {
    parts_desc.push(`in month ${month}`);
  }

  // Weekday
  if (weekday !== "*") {
    const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    if (weekday.includes("-")) {
      const [start, end] = weekday.split("-");
      parts_desc.push(`on ${weekdayNames[parseInt(start)]} through ${weekdayNames[parseInt(end)]}`);
    } else if (weekday.includes(",")) {
      const days = weekday.split(",").map(d => weekdayNames[parseInt(d)]);
      parts_desc.push(`on ${days.join(", ")}`);
    } else {
      parts_desc.push(`on ${weekdayNames[parseInt(weekday)]}`);
    }
  }

  return parts_desc.join(" ");
}

/**
 * Common cron expression presets
 */
export const cronPresets = [
  { name: "Every day at 9:00 AM", expression: "0 9 * * *" },
  { name: "Every day at 2:30 PM", expression: "30 14 * * *" },
  { name: "Every 6 hours", expression: "0 */6 * * *" },
  { name: "Every 15 minutes", expression: "*/15 * * * *" },
  { name: "Weekdays at 9:00 AM", expression: "0 9 * * 1-5" },
  { name: "Weekends at 10:00 AM", expression: "0 10 * * 0,6" },
  { name: "First day of month at midnight", expression: "0 0 1 * *" },
  { name: "Every Monday at 8:00 AM", expression: "0 8 * * 1" },
  { name: "Twice daily (9 AM and 6 PM)", expression: "0 9,18 * * *" },
  { name: "Every hour during business hours", expression: "0 9-17 * * 1-5" },
];